import { useState, useEffect, useContext, useRef } from "react";
import EditableImage from "./EditableImage";
import { domain } from "../consts";
import { AlertContext } from "../context/AlertContext";
import TextOptionsMenu from "./TextOptionsMenu";
import { MenuSVG } from "./SVG";

export default function InvitationCounter({
  rsvp,
  isEditable = false,
  onUpdate,
}) {
  const defaultImage = "https://bucket.pozivnica.hr/pozivnica/counter.webp";
  const imageUrl = rsvp?.countdownImage || defaultImage;

  const { handleAlert } = useContext(AlertContext);
  const [isUploadingCounter, setIsUploadingCounter] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // For text editing functionality
  const [showOptions, setShowOptions] = useState(false);
  const [optionsPosition, setOptionsPosition] = useState({ left: 0, top: 0 });
  const buttonRef = useRef(null);
  const optionsRef = useRef(null);

  function formatTimeUnit(unit, singular, plural, pluralGenitive) {
    if (unit === 1) {
      return singular;
    } else if (unit > 1 && unit < 5) {
      return plural;
    } else {
      return pluralGenitive;
    }
  }

  const handleButtonClick = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    setOptionsPosition({
      top: rect.bottom + 25,
      left: window.innerWidth / 2,
    });

    setShowOptions((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showOptions &&
        optionsRef.current &&
        !optionsRef.current.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOptions]);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +new Date(rsvp.date) - +new Date();
      let timeLeft = {};

      if (difference > 0) {
        timeLeft = {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        };
      }

      return timeLeft;
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [rsvp.date]);

  return (
    <div className="relative text-center">
      <div className="relative">
        <EditableImage
          imageUrl={imageUrl}
          defaultImage={defaultImage}
          onImageChange={(updates) => onUpdate(updates)}
          isEditable={isEditable}
          className="w-full h-full object-cover"
          imageType="countdownImage"
          setIsUploading={setIsUploadingCounter}
          isUploading={isUploadingCounter}
          handleAlert={handleAlert}
          domain={domain}
          includeImage={rsvp.includeCountdownImage}
        />

        <div
          className={
            rsvp.includeCountdownImage
              ? "absolute h-[60px] top-20 inset-0 flex items-center justify-center z-10"
              : "my-12"
          }
        >
          <div className="group relative w-full">
            <div
              className={`w-full font-${
                rsvp.countdownFont || rsvp.textFont
              } grid grid-cols-4 gap-5 text-center ${
                rsvp.includeCountdownImage ? "bg-gray-800" : "bg-transparent"
              } bg-opacity-50 py-2 w-full`}
              style={{
                color: rsvp.countdownColor || "white",
                fontSize: rsvp.countdownFontSize
                  ? `${parseInt(rsvp.countdownFontSize, 10)}px`
                  : "16px",
              }}
            >
              {Object.keys(timeLeft).length > 0 ? (
                <>
                  <div
                    className={`${rsvp.countdownIsBold ? "font-bold" : ""} ${
                      rsvp.countdownIsItalic ? "italic" : ""
                    } font-${rsvp.countdownFont}`}
                    style={{
                      color: rsvp.countdownColor,
                    }}
                  >
                    <p style={{ color: rsvp.countdownColor }}>
                      {timeLeft.days}
                    </p>
                    <p className="text-sm font-normal">
                      {formatTimeUnit(timeLeft.days, "Dan", "Dana", "Dana")}
                    </p>
                  </div>
                  <div
                    className={`${rsvp.countdownIsBold ? "font-bold" : ""} ${
                      rsvp.countdownIsItalic ? "italic" : ""
                    } font-${rsvp.countdownFont}`}
                    style={{
                      color: rsvp.countdownColor,
                    }}
                  >
                    <p style={{ color: rsvp.countdownColor }}>
                      {timeLeft.hours}
                    </p>
                    <p className="text-sm font-normal">
                      {formatTimeUnit(timeLeft.hours, "Sat", "Sata", "Sati")}
                    </p>
                  </div>
                  <div
                    className={`${rsvp.countdownIsBold ? "font-bold" : ""} ${
                      rsvp.countdownIsItalic ? "italic" : ""
                    } font-${rsvp.countdownFont}`}
                    style={{
                      color: rsvp.countdownColor,
                    }}
                  >
                    <p style={{ color: rsvp.countdownColor }}>
                      {timeLeft.minutes}
                    </p>
                    <p className="text-sm font-normal">
                      {formatTimeUnit(
                        timeLeft.minutes,
                        "Minuta",
                        "Minute",
                        "Minuta",
                      )}
                    </p>
                  </div>
                  <div
                    className={`${rsvp.countdownIsBold ? "font-bold" : ""} ${
                      rsvp.countdownIsItalic ? "italic" : ""
                    } font-${rsvp.countdownFont}`}
                    style={{
                      color: rsvp.countdownColor,
                    }}
                  >
                    <p style={{ color: rsvp.countdownColor }}>
                      {" "}
                      {timeLeft.seconds}
                    </p>
                    <p
                      className="text-sm font-normal"
                      style={{ color: rsvp.countdownColor }}
                    >
                      {formatTimeUnit(
                        timeLeft.seconds,
                        "Sekunda",
                        "Sekunde",
                        "Sekundi",
                      )}
                    </p>
                  </div>
                </>
              ) : (
                <div
                  className={`${rsvp.countdownIsBold ? "font-bold" : ""} ${
                    rsvp.countdownIsItalic ? "italic" : ""
                  } font-${rsvp.countdownFont} col-span-4`}
                  style={{
                    color: rsvp.countdownColor,
                    fontSize: rsvp.countdownFontSize,
                  }}
                >
                  Time is up!
                </div>
              )}
            </div>

            {isEditable && (
              <button
                type="button"
                ref={buttonRef}
                onClick={handleButtonClick}
                className="absolute -top-8 opacity-0 group-hover:opacity-100 transition-opacity bg-teal-600 rounded-full"
              >
                <MenuSVG />
              </button>
            )}

            {showOptions && (
              <div
                ref={optionsRef}
                className="fixed bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-[999] transform -translate-x-1/2"
                style={{
                  left: `${optionsPosition.left}px`,
                  top: `${optionsPosition.top}px`,
                }}
              >
                <TextOptionsMenu
                  font={rsvp.countdownFont}
                  color={rsvp.countdownColor}
                  fontSize={rsvp.countdownFontSize}
                  isBold={rsvp.countdownIsBold}
                  isItalic={rsvp.countdownIsItalic}
                  onStyleChange={(updates) => {
                    console.log(updates);
                    const mappedUpdates = {};

                    if (updates.font !== undefined) {
                      mappedUpdates.countdownFont = updates.font;
                    }

                    if (updates.color !== undefined) {
                      mappedUpdates.countdownColor = updates.color;
                    }

                    if (updates.fontSize !== undefined) {
                      // Ensure fontSize is treated as a number
                      mappedUpdates.countdownFontSize = parseInt(
                        updates.fontSize,
                        10,
                      );
                    }
                    if (updates.isBold !== undefined) {
                      mappedUpdates.countdownIsBold = updates.isBold;
                    }
                    if (updates.isItalic !== undefined) {
                      mappedUpdates.countdownIsItalic = updates.isItalic;
                    }

                    console.log("Updating countdown with:", mappedUpdates);
                    onUpdate(mappedUpdates);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
